import { tr, getLanguage, spanishShortDate } from "./i18n.js";
/**
 * When a suggested source was published — display rules.
 *
 * "Find more sources" returns each candidate's publication date
 * (`published_at`, ISO-8601 UTC, from the Google News feed). An
 * interesting headline that turns out to be a year old is not a source
 * worth adding, so the date sits beside the source name and old ones
 * are coloured.
 *
 * Pure and framework-free — node --test frontend/scripts/sourceDate.test.mjs
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY = 24 * 60 * 60 * 1000;

/** Older than this is "old" (amber). */
export const OLD_AFTER_DAYS = 30;
/** Older than this is "stale" (red): the "from a year ago" case. */
export const STALE_AFTER_DAYS = 365;

/** "2026-10-03T14:00:00+00:00" -> "Oct 3, 2026" (UTC, no shifting). */
export function formatSourceDate(iso) {
  const t = Date.parse(iso || "");
  if (Number.isNaN(t)) return "";
  const d = new Date(t);
  if (getLanguage() === "es") {
    return spanishShortDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** Whole days between the publication day and today (UTC days). */
export function daysOld(iso, now = Date.now()) {
  const t = Date.parse(iso || "");
  if (Number.isNaN(t)) return null;
  const a = Math.floor(t / DAY);
  const b = Math.floor(now / DAY);
  return Math.max(0, b - a);
}

/** "today", "yesterday", "4 days ago", "3 months ago", "2 years ago". */
export function relativeAge(days) {
  if (days === null || days === undefined) return "";
  if (days <= 0) return tr("today");
  if (days === 1) return tr("yesterday");
  if (days < 60) return tr("{days} days ago", { days });
  if (days < 365) return tr("{floor} months ago", { floor: Math.floor(days / 30) });
  const years = Math.floor(days / 365);
  if (days < 730) return tr("over a year ago");
  return tr("{years} years ago", { years });
}

/** "fresh" | "old" | "stale" | "unknown" */
export function ageTone(days) {
  if (days === null || days === undefined) return "unknown";
  if (days > STALE_AFTER_DAYS) return "stale";
  if (days > OLD_AFTER_DAYS) return "old";
  return "fresh";
}

/** Everything the tag needs: `{ text, tone, title }`. */
export function sourceDateInfo(iso, now = Date.now()) {
  const days = daysOld(iso, now);
  if (days === null) {
    return {
      text: tr("date unknown"),
      tone: "unknown",
      title: tr("The source did not give a publication date. Open it to check."),
    };
  }
  const tone = ageTone(days);
  return {
    text: `${formatSourceDate(iso)} · ${relativeAge(days)}`,
    tone,
    title:
      tone === "stale"
        ? tr("Published more than a year ago")
        : tone === "old"
          ? tr("Published more than a month ago")
          : tr("Publication date"),
  };
}
